"use client";

import {useQuery} from "@tanstack/react-query";

import {useSearchParams} from "next/navigation";

import FetchError from "@/components/ui/fetch-error";
import Loading from "@/components/ui/loading";
import {Row} from "react-bootstrap";

import OverviewTable from "./overview-table";

import api from "@/lib/api";

type SurveyOverviewProps = {
  study: {
    id: number;
    title: string;
    full_name: string;
    has_overview: boolean;
  };
  countries: string;
  years_collected: number;
  study_type: string;
  rounds: string;
  covid_full_name: string;
};

export default function OverviewHome() {
  const searchParams = useSearchParams();
  const studyIdParam = searchParams.get("studyId");

  const {
    data: overviews = [],
    error,
    isPending,
  } = useQuery({
    queryKey: ["srv-covid-overview"],
    queryFn: () => api.get<SurveyOverviewProps[]>("/srv/home/?site_name=covid"),
  });

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;

  const studyId = studyIdParam ? Number(studyIdParam) : undefined;

  return (
    <Row>
      <p>
        The Health and Retirement Study (HRS) began in 1992 as a nationally
        representative, longitudinal, multidisciplinary survey of individuals
        over age 50 in the United States. Since then, it has been fielded every
        2 years with periodic refresher samples to maintain a nationally
        representative sample over time. As population aging has progressed in
        every region of the world, the success of the HRS generated substantial
        interest in collecting similar data in a similar manner, leading to the
        development of a number of surveys designed to be comparable with the
        HRS.
      </p>

      <p>
        The HRS family of studies share some common characteristics including
        being population representative of older adults, longitudinal,
        multidisciplinary, having coordinated survey instruments, enhanced
        economic data, and integrated biomarkers. All HRS family studies
        distribute their data to the research community.
      </p>

      <p className='mb-5'>
        Shortly after the COVID-19 pandemic began, the HRS family of studies
        began collecting data aimed at understanding exactly how the pandemic
        has impacted the lives of millions of older individuals globally. While
        some studies added COVID-19 specific questions to their core or
        self-completion interview, others conducted entirely separate COVID-19
        interviews, and some did a combination of both. Some studies collected a
        single round of COVID-19 interviews, and several studies collected data
        at multiple time points between 2020 and 2022.
      </p>

      <h4 className='g2-header-navy'>Overview</h4>
      <hr />
      <p>
        Find information about each study with COVID interview catalogued by the
        Gateway
      </p>

      <OverviewTable overviews={overviews} initialStudyId={studyId} />
    </Row>
  );
}
